import type { I18nLocale } from "@/modules/i18n/types"
import type { Locale as LocaleDateFns } from "date-fns"

import { minutesToHours } from "date-fns"
import localeDateCa from "date-fns/locale/ca"
import localeDateCs from "date-fns/locale/cs"
import localeDateDa from "date-fns/locale/da"
import localeDateDe from "date-fns/locale/de"
import localeDateEn from "date-fns/locale/en-GB"
import localeDateEs from "date-fns/locale/es"
import localeDateFi from "date-fns/locale/fi"
// FIXME : dig into how we could import only current local.
// Currently, all below locals are only import on pages that need them.
// See https://github.com/date-fns/date-fns/blob/main/docs/webpack.md
import localeDateFr from "date-fns/locale/fr"
import localeDateGl from "date-fns/locale/gl"
import localeDateIt from "date-fns/locale/it"
import localeDateNb from "date-fns/locale/nb"
import localeDateNl from "date-fns/locale/nl"
import localeDatePl from "date-fns/locale/pl"
import localeDatePt from "date-fns/locale/pt"
import localeDateSv from "date-fns/locale/sv"

import { DEFAULT_LOCALE } from "@/modules/locales/constants"

export const DATE_FNS_FORMATS = {
  // e.g. 23/04/2020
  DEFAULT_DATE: "dd/MM/yyyy",
  DEFAULT_DATE_FNS: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  FIRST_DAY_OF_MONTH: "yyyy-MM-01",
  // e.g. Tuesday 23 April
  FULL_DATE: "iiii dd MMMM",
  // e.g. Tuesday 23 April - 15:36
  FULL_DATETIME: "iiii dd MMMM - HH:mm",
  FULL_DATE_DE: "iiii, dd. MMMM",
  FULL_DATE_EN: "iiii, dd MMMM",
  // e.g. 23 April 2020
  LIGHT_DATE: "dd MMMM yyyy",
  // e.g. 23. April 2020
  LIGHT_DATE_DE: "dd. MMMM yyyy",
  // e.g. 23 April
  LIGHT_DATE_WITHOUT_YEAR: "dd MMMM",
  // e.g. 23. April
  LIGHT_DATE_WITHOUT_YEAR_DE: "dd. MMMM",
  // e.g. Apr 2020
  MONTH_YEAR: "MMM yyyy",
  // e.g. 15:36
  TIME: "HH:mm",
  // e.g. 03:36 PM
  TIME_EN: "hh:mm b",
}

export const getCurrentLocaleDate = (locale = DEFAULT_LOCALE): LocaleDateFns => {
  switch (locale) {
    case "fr":
      return localeDateFr
    case "en":
      return localeDateEn
    case "ca":
      return localeDateCa
    case "da":
      return localeDateDa
    case "de":
      return localeDateDe
    case "es":
      return localeDateEs
    case "it":
      return localeDateIt
    case "no":
      return localeDateNb
    case "nl":
      return localeDateNl
    case "pt":
      return localeDatePt
    case "pl":
      return localeDatePl
    case "fi":
      return localeDateFi
    case "gl":
      return localeDateGl
    case "cs":
      return localeDateCs
    case "sv":
      return localeDateSv
    default:
      return localeDateEn
  }
}

export const getDefaultDateFormat = (): string => DATE_FNS_FORMATS.DEFAULT_DATE

export const getLightDateFormatFromLocale = (locale: I18nLocale, hasYear = true): string => {
  if (hasYear && locale === "de") return DATE_FNS_FORMATS.LIGHT_DATE_DE
  if (hasYear) return DATE_FNS_FORMATS.LIGHT_DATE
  if (locale === "de") return DATE_FNS_FORMATS.LIGHT_DATE_WITHOUT_YEAR_DE
  return DATE_FNS_FORMATS.LIGHT_DATE_WITHOUT_YEAR
}

export const getTimeFormatFromLocale = (locale: I18nLocale): string =>
  locale === "en" ? DATE_FNS_FORMATS.TIME_EN : DATE_FNS_FORMATS.TIME

const formatMinutesToHours = (minutes: number): string => {
  const hours = minutesToHours(minutes)
  const minutesLeft = minutes - hours * 60
  const stringifiedHours = String(hours).length === 1 ? `0${hours}` : `${hours}`
  const stringifiedMinutes = String(minutesLeft).length === 1 ? `0${minutesLeft}` : `${minutesLeft}`
  return `${stringifiedHours}:${stringifiedMinutes}`
}

export const getFormattedTimeZoneOffset = (date: Date): string => {
  const offsetInMinutes = date.getTimezoneOffset()

  const formatted = formatMinutesToHours(Math.abs(offsetInMinutes))

  // Negative offset means the local time zone is ahead of UTC.
  if (offsetInMinutes < 0) {
    return `(UTC+${formatted})`
  }
  return `(UTC-${formatted})`
}

export const getFullDateFormatFromLocale = (locale: I18nLocale): string => {
  if (locale === "en") return DATE_FNS_FORMATS.FULL_DATE_EN
  if (locale === "de") return DATE_FNS_FORMATS.FULL_DATE_DE
  return DATE_FNS_FORMATS.FULL_DATE
}
